/**
 * Command Bar Button - Compact trigger for Autonomous Command Bar
 * Sits in the header navigation
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Command, 
  Mic, 
  MicOff, 
  X, 
  Loader2,
  CheckCircle,
  Sparkles,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Wake words
const WAKE_WORDS = ['hey job', 'hey manager', 'okay job'];

export default function CommandBarButton({ isLandingPage = false }) {
  const navigate = useNavigate();
  
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [commandInput, setCommandInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [lastResult, setLastResult] = useState(null);
  
  // Refs
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const dropdownRef = useRef(null);

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        if (interimTranscript) {
          setVoiceTranscript(interimTranscript);
        }
        
        if (finalTranscript) {
          setCommandInput(finalTranscript);
          setVoiceTranscript('');
          executeCommand(finalTranscript);
        }
      };
      
      recognitionRef.current.onerror = (event) => {
        if (event.error !== 'no-speech') {
          setIsListening(false);
        }
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
      
      setIsVoiceEnabled(true);
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      if (e.key === 'Escape') {
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(false);
        }
      }
    };
    
    // Use capture phase to catch Escape before other handlers
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Toggle voice
  const toggleVoice = () => {
    if (!isVoiceEnabled) {
      toast.error('Voice not supported in this browser');
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
        toast.success('Listening...');
      } catch (e) {
        toast.error('Failed to start voice');
      }
    }
  };

  // Execute command
  const executeCommand = async (command = commandInput) => {
    if (!command.trim()) return;
    
    setIsProcessing(true);
    setLastResult(null);
    
    try {
      const response = await axios.post(`${API}/api/ai-skills/extract-action`, {
        user_message: command,
        context: { autonomous_mode: true }
      });
      
      const { has_action, action, response_text } = response.data;
      
      if (has_action && action) {
        setLastResult({ type: 'success', message: response_text });
        
        if (action.route) {
          toast.success(response_text);
          setTimeout(() => {
            navigate(action.route);
            setIsOpen(false);
            setCommandInput('');
          }, 300);
        } else if (action.action_type === 'search_providers' || action.action_type === 'search_jobs') {
          const execRes = await axios.post(
            `${API}/api/ai-skills/execute?skill_id=${action.skill_id}`,
            action.parameters
          );
          const count = execRes.data.results?.length || 0;
          setLastResult({ type: 'success', message: `Found ${count} results` });
          toast.success(`Found ${count} results`);
        }
      } else {
        // Send to AI for response
        const chatRes = await axios.post(`${API}/api/ai-agent/chat`, {
          session_id: `cmd_${Date.now()}`,
          message: command,
          context: { autonomous_mode: true }
        });
        
        setLastResult({ type: 'info', message: chatRes.data.response?.substring(0, 100) + '...' });
      }
    } catch (error) {
      setLastResult({ type: 'error', message: 'Command failed' });
      toast.error('Command failed');
    } finally {
      setIsProcessing(false);
    }
  };

  // Quick commands
  const quickCommands = [
    { label: 'Dashboard', cmd: 'Go to dashboard' },
    { label: 'Jobs', cmd: 'Show available jobs' },
    { label: 'Estimator', cmd: 'Open estimator' },
    { label: 'Projects', cmd: 'Show my projects' },
  ];

  const buttonStyles = isLandingPage 
    ? 'bg-white/20 hover:bg-white/30 text-white border-white/20' 
    : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${buttonStyles}`}
        data-testid="command-bar-btn"
      >
        <Command className="w-4 h-4" />
        <span className="hidden sm:inline">Command</span>
        <kbd className={`hidden md:inline px-1.5 py-0.5 rounded text-xs ${isLandingPage ? 'bg-white/20' : 'bg-slate-200'}`}>
          ⌘K
        </kbd>
      </button>

      {/* Dropdown Command Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-slate-900 rounded-xl shadow-2xl border border-slate-700 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Input Area */}
          <div className="p-3 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <button
                onClick={toggleVoice}
                disabled={!isVoiceEnabled}
                className={`p-2 rounded-lg transition-all ${
                  isListening 
                    ? 'bg-red-500 text-white animate-pulse' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
                title={isListening ? 'Stop' : 'Voice command'}
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              
              <input
                ref={inputRef}
                type="text"
                value={voiceTranscript || commandInput}
                onChange={(e) => setCommandInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && executeCommand()}
                placeholder={isListening ? 'Listening...' : 'Type command or speak...'}
                className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                disabled={isProcessing}
                autoFocus
              />
              
              <button
                onClick={() => executeCommand()}
                disabled={isProcessing || !commandInput.trim()}
                className="p-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-600 text-white rounded-lg transition-all"
              >
                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              </button>
              
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 bg-slate-700 text-slate-300 hover:bg-slate-600 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Quick Commands */}
          <div className="p-2 border-b border-slate-700">
            <p className="text-xs text-slate-500 px-2 mb-2">Quick Commands</p>
            <div className="flex flex-wrap gap-1">
              {quickCommands.map((qc, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setCommandInput(qc.cmd);
                    executeCommand(qc.cmd);
                  }}
                  className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-md transition-all"
                >
                  {qc.label}
                </button>
              ))}
            </div>
          </div>

          {/* Result Display */}
          {lastResult && (
            <div className={`p-3 text-sm ${
              lastResult.type === 'success' ? 'bg-emerald-900/50 text-emerald-300' :
              lastResult.type === 'error' ? 'bg-red-900/50 text-red-300' :
              'bg-slate-800 text-slate-300'
            }`}>
              <div className="flex items-center gap-2">
                {lastResult.type === 'success' && <CheckCircle className="w-4 h-4" />}
                <span className="truncate">{lastResult.message}</span>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-3 py-2 bg-slate-800/50 text-center">
            <p className="text-xs text-slate-500">
              <kbd className="px-1 bg-slate-700 rounded">↵</kbd> run • 
              <kbd className="px-1 bg-slate-700 rounded ml-2">esc</kbd> close
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
